/** @format */

import { useEffect, useState } from "react";
import DrawerNavbar from "../drawerNavBar/DrawerNavbar";
import UserProfileHeader from "../userprofile-header/UserProfileHeader";
import {
	Alert,
	Box,
	Button,
	Card,
	Chip,
	CardActions,
	CardContent,
	CardHeader,
	Checkbox,
	Container,
	Divider,
	FormControlLabel,
	Grid,
	Stack,
	TextField,
	Typography,
	alpha,
} from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { Form, Formik } from "formik";
import { circleLeaderApplicationValidation } from "../../utility/validation/circleLeaderApplicationValidation";
import { ICircleLeaderApplicationPayload } from "../../utility/types/circleType";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "../../redux/selectors/userSelector";
import { colors } from "../../utility/types/constant";
import { SectionWrapper, StyledTypography } from "./styles";
import { AppDispatch } from "../../redux/store/store";
import { circleLeaderApplication } from "../../redux/actions/circleAction";
import {
	AccountVerificationModal,
	LeaderApplicationTermsModal,
	LoadingSpinner,
	NotificationModal,
} from "../allComponents/AllComponents";
import { selectIsLoading } from "../../redux/selectors/circleSelector";
import { useNavigate } from "react-router-dom";

const CircleLeaderApplication = () => {
	const navigate = useNavigate();

	const dispatch = useDispatch<AppDispatch>();

	const userData = useSelector(selectCurrentUser);

	const isLoading = useSelector(selectIsLoading);

	const [openNav, setOpenNav] = useState(false);

	const [showVerifyModal, setShowVerifyModal] = useState(false);

	const [showNotificationModal, setShowNotificationModal] = useState(false);

	const [successAlert, setSuccessAlert] = useState(false);

	const [errorAlert, setErrorAlert] = useState("");

	const [showTerms, setShowTerms] = useState(false);

	const [initialValues, setInitialValues] =
		useState<ICircleLeaderApplicationPayload>({
			userId: userData?._id || "",
			email: userData?.email || "",
			reason: "",
			hasAgreedToTerms: false,
		});

	const handleShowTerms = () => {
		setShowTerms(true);
	};

	const handleAgreeToTerms = () => {
		setInitialValues((prevValues) => ({
			...prevValues,
			hasAgreedToTerms: true,
		}));
		setShowTerms(false);
	};

	const handleDisAgreeToTerms = () => {
		setInitialValues((prev) => ({ ...prev, hasAgreedToTerms: false }));
		setShowTerms(false);
		setErrorAlert(
			"You must agree to the terms and conditions to submit the application.",
		);
		setTimeout(() => {
			setErrorAlert("");
		}, 5000);
	};

	const handleCloseShowTerms = () => {
		setShowTerms(false);
	};

	const handleSubmit = async (payloadData: ICircleLeaderApplicationPayload) => {
		if (!userData?._id) {
			setErrorAlert("No User ID. Cannot submit the application.");
			return;
		}

		if (!initialValues.hasAgreedToTerms) {
			setErrorAlert(
				"You must agree to the terms and conditions to submit the application.",
			);
			setTimeout(() => {
				setErrorAlert("");
			}, 5000);

			return;
		}

		try {
			const payload = {
				...payloadData,
				userId: userData?._id,
				hasAgreedToTerms: initialValues.hasAgreedToTerms,
			};

			const data = await dispatch(circleLeaderApplication(payload)).unwrap();

			if (data) {
				setSuccessAlert(true);

				if (userData?.isVerified) {
					setShowNotificationModal(true);

					setTimeout(() => {
						setShowNotificationModal(false);
						navigate(`/user/${userData?._id}`);
					}, 20000);
				} else {
					setShowVerifyModal(true);
				}
			} else {
				throw new Error("Application submission was unsuccessful.");
			}
		} catch (rejectedValueOrSerializedError) {
			// Handle errors from the thunk
			setErrorAlert("Duplicate Application Or An Unknown error occurred");
			setSuccessAlert(false);
			setShowVerifyModal(false);
		}
	};

	const handleCloseSuccessAlert = () => {
		setSuccessAlert(false);
	};

	const handleCloseErrorAlert = () => {
		setSuccessAlert(false);
		setErrorAlert("");
	};

	useEffect(() => {
		let timeOutId: any;
		return () => {
			if (timeOutId) {
				clearTimeout(timeOutId);
			}
		};
	});

	return (
		<>
			<UserProfileHeader onOpenNav={() => setOpenNav(true)} />

			<Box
				component="main"
				sx={{
					flexGrow: 1,
					py: 8,
					justifyContent: "center",
					display: "flex",
				}}>
				<DrawerNavbar openNav={openNav} onCloseNav={() => setOpenNav(false)} />

				<Container
					maxWidth="lg"
					sx={{
						mt: 7,
					}}>
					{successAlert && (
						<Stack sx={{ width: "100%" }} spacing={2}>
							<Alert severity="success" onClose={handleCloseSuccessAlert}>
								Success -{" "}
								<strong>
									Application Successfully sent. Please verify your account
									in-order for your application process to be completed
								</strong>
							</Alert>
						</Stack>
					)}

					{errorAlert && (
						<Stack sx={{ width: "100%" }} spacing={2}>
							<Alert severity="error" onClose={handleCloseErrorAlert}>
								Error -{" "}
								<strong>
									{typeof errorAlert === "string" ?
										errorAlert
									:	"An error occurred. Duplicate application or please try later."
									}
								</strong>
							</Alert>
						</Stack>
					)}

					<Stack spacing={8} sx={{ width: "100%" }}>
						<div>
							<Typography variant="h4">Be A Leader</Typography>
						</div>

						{!userData?.isVerified && (
							<Box
								sx={{
									position: "relative",
									overflow: "hidden",
									borderRadius: 3,
									border: "1px solid",
									borderColor: alpha(colors.mainSecondary, 0.3),
									background: `linear-gradient(135deg, ${alpha(colors.primary, 0.06)} 0%, ${alpha(colors.mainSecondary, 0.04)} 100%)`,
									p: { xs: 3, md: 4 },
								}}>
								{/* Decorative background icon */}
								<ShieldOutlinedIcon
									sx={{
										position: "absolute",
										top: -10,
										right: -10,
										fontSize: 140,
										opacity: 0.05,
										color: colors.mainSecondary,
										transform: "rotate(15deg)",
										pointerEvents: "none",
									}}
								/>

								<Stack
									direction={{ xs: "column", sm: "row" }}
									spacing={3}
									alignItems={{ xs: "flex-start", sm: "center" }}>
									<Box
										sx={{
											width: 56,
											height: 56,
											minWidth: 56,
											borderRadius: "50%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, ${colors.primary} 100%)`,
											boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
										}}>
										<VerifiedUserOutlinedIcon
											sx={{ fontSize: 28, color: "#fff" }}
										/>
									</Box>

									<Box sx={{ flex: 1 }}>
										<Stack
											direction="row"
											alignItems="center"
											spacing={1}
											sx={{ mb: 0.5 }}>
											<Typography
												variant="h6"
												sx={{
													fontWeight: 700,
													color: colors.primary,
													letterSpacing: "-0.01em",
												}}>
												Identity Verification Required
											</Typography>
											<Chip
												label="Action Needed"
												size="small"
												variant="outlined"
												sx={{
													fontWeight: 600,
													fontSize: "0.7rem",
													color: colors.mainSecondary,
													borderColor: colors.mainSecondary,
												}}
											/>
										</Stack>

										<Typography
											variant="body2"
											sx={{
												color: "text.secondary",
												lineHeight: 1.7,
												maxWidth: 520,
											}}>
											To create a circle and become a Circle Leader or Host, you
											must first verify your identity. Head over to your{" "}
											<strong>Dashboard</strong> to complete the verification
											process, it only takes a few minutes.
										</Typography>
									</Box>

									<Button
										variant="contained"
										endIcon={<ArrowForwardIcon />}
										onClick={() => navigate(`/user/${userData?._id}`)}
										sx={{
											minWidth: 180,
											px: 3,
											py: 1.2,
											borderRadius: 2,
											fontWeight: 600,
											textTransform: "none",
											color: colors.white,
											background: `linear-gradient(135deg, ${colors.mainSecondary} 0%, ${colors.primary} 100%)`,
											boxShadow: `0 4px 12px ${alpha(colors.mainSecondary, 0.35)}`,
											"&:hover": {
												background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.mainSecondary} 100%)`,
												boxShadow: `0 6px 16px ${alpha(colors.mainSecondary, 0.45)}`,
											},
										}}>
										Go to Dashboard
									</Button>
								</Stack>
							</Box>
						)}

						<div>
							<Grid container spacing={5}>
								<Grid item xs={12} md={6} lg={4} sx={{ padding: 2 }}>
									<Card>
										<CardContent>
											<SectionWrapper>
												<StyledTypography>
													As a Circle Leader on FundsyFi, you’ll inspire
													financial success, foster community bonds, and enhance
													your leadership skills. Lead the way to collective
													prosperity and personal growth in just a few
													steps.{" "}
												</StyledTypography>
											</SectionWrapper>
										</CardContent>
									</Card>
								</Grid>

								<Grid item xs={12} md={6} lg={8} sx={{ padding: 2 }}>
									<Formik
										initialValues={initialValues}
										onSubmit={handleSubmit}
										validationSchema={circleLeaderApplicationValidation}>
										{({ values, errors, touched, handleChange }) => (
											<Form>
												<Card>
													<CardHeader
														subheader="Please fill in the fields"
														title="Circle Leader Application"
													/>

													<CardContent>
														<Box sx={{ m: 5 }}>
															<Grid container spacing={3}>
																<Grid item xs={12} md={12}>
																	<TextField
																		fullWidth
																		label="Email"
																		name="email"
																		onChange={handleChange}
																		value={values.email}
																		error={!!(errors?.email && touched?.email)}
																		helperText={
																			errors.email &&
																			touched.email &&
																			errors.email
																		}
																		sx={{
																			fontSize: "1.2rem",
																			"& .MuiOutlinedInput-root": {
																				"& fieldset": {
																					borderColor: "black",
																				},
																				"&:hover fieldset": {
																					borderColor: "black",
																				},
																				"&.Mui-focused fieldset": {
																					borderColor: "black",
																				},
																			},
																		}}
																	/>
																</Grid>

																<Grid item xs={12} md={12}>
																	<TextField
																		fullWidth
																		label="Reason"
																		name="reason"
																		multiline
																		rows={4}
																		onChange={handleChange}
																		value={values.reason}
																		error={
																			!!(errors?.reason && touched?.reason)
																		}
																		helperText={
																			errors.reason &&
																			touched.reason &&
																			errors.reason
																		}
																		sx={{
																			fontSize: "1.2rem",
																			"& .MuiOutlinedInput-root": {
																				"& fieldset": {
																					borderColor: "black",
																				},
																				"&:hover fieldset": {
																					borderColor: "black",
																				},
																				"&.Mui-focused fieldset": {
																					borderColor: "black",
																				},
																			},
																		}}
																	/>
																</Grid>
															</Grid>

															<FormControlLabel
																control={
																	<Checkbox
																		value="remember"
																		color="primary"
																		onChange={handleShowTerms}
																	/>
																}
																label="Agree To Terms"
															/>

															<LeaderApplicationTermsModal
																title="Circle Leader Application Terms & Conditions"
																open={showTerms}
																handleClose={handleCloseShowTerms}
																onAgree={handleAgreeToTerms}
																onDisAgree={handleDisAgreeToTerms}
															/>
														</Box>
													</CardContent>

													<Divider sx={{ backgroundColor: "black" }} />

													<CardActions sx={{ justifyContent: "center", py: 2 }}>
														<Button
															disabled={isLoading || !userData?.isVerified}
															variant="contained"
															type="submit"
															sx={{
																color: colors.white,
																backgroundColor: colors.mainSecondary,
																"&:hover": {
																	backgroundColor: colors.mainSecondary,
																},
																"&.Mui-disabled": {
																	backgroundColor: (theme: any) =>
																		alpha(theme.palette.action.disabled, 0.12),
																	color: (theme: any) =>
																		theme.palette.action.disabled,
																},
																width: 150,
																transition: "all 0.2s ease-in-out",
															}}>
															{isLoading ?
																<LoadingSpinner loading={isLoading} />
															:	"Send"}
														</Button>
													</CardActions>
												</Card>
											</Form>
										)}
									</Formik>
								</Grid>
							</Grid>
						</div>
					</Stack>
					{showNotificationModal && (
						<NotificationModal
							open={showNotificationModal}
							handleClose={() => setShowNotificationModal(false)}>
							<StyledTypography
								sx={{ fontSize: "1.1rem", color: "text.secondary" }}>
								Thanks for your application, we are currently reviewing your
								application and please expect a period of 24 hours in order to
								have updates on your application.
								<br />
								You will be notified via your email and notification in your
								profile. Thank you for your patience.
								<br />
								We will be redirecting you to your profile shortly.
							</StyledTypography>
						</NotificationModal>
					)}
				</Container>
			</Box>

			{showVerifyModal && (
				<AccountVerificationModal
					open={showVerifyModal}
					handleClose={() => setShowVerifyModal(false)}
				/>
			)}
		</>
	);
};

export default CircleLeaderApplication;
